import qs from "query-string";

import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useEffect } from "react";
import { useHiddenMetricsByComponent } from "components/Dashboard/GroupMetrics/useVisibleMetrics";

/**
 * when the preferred metric changes,
 * make sure the associated metric is visible in GroupMetrics
 * otherwise we get "undefined %" in TrendChartCenterMetric
 */
export function useEnsurePreferredMetricIsVisibleInMetricRow() {
  const { preferredMetric } = useSettings();
  const [hiddenMetricsByComponent, setHiddenMetricsByComponent] =
    useHiddenMetricsByComponent();
  useEffect(() => {
    if (hiddenMetricsByComponent.GroupMetrics.includes(preferredMetric)) {
      setHiddenMetricsByComponent({
        ...hiddenMetricsByComponent,
        GroupMetrics: hiddenMetricsByComponent.GroupMetrics.filter(
          metric => metric !== preferredMetric
        ),
      });
    }
  }, [hiddenMetricsByComponent, preferredMetric, setHiddenMetricsByComponent]);
}

/** for each setting in local storage, apply it to the SettingsManager */
export function applySettingsFromLocalStorage(
  settingsToApply: {
    current: boolean;
    localStorageName: string;
    updateFn: (lsString: string) => void;
  }[],
  ckey: string,
  shouldInitializeLocalStorage: boolean
) {
  settingsToApply.forEach(({ current, localStorageName, updateFn }) => {
    const localStorageString = window.localStorage.getItem(localStorageName);

    const isMetricSetting = ["preferredMetric"].includes(localStorageName);

    const isSettingAlreadyPopulated = Boolean(localStorageString);

    /**
     * Populate customer's local storage metric settings on first time app load,
     * so that there's never an edge case of the local storage settings not existing
     */
    const doOverwriteLocalStorage =
      isMetricSetting &&
      shouldInitializeLocalStorage &&
      !isSettingAlreadyPopulated &&
      ckey &&
      ckey !== "";

    const doOverwriteStoreSetting = !doOverwriteLocalStorage;

    if (
      doOverwriteStoreSetting &&
      localStorageString !== null && // don't overwrite the default setting with a null value
      localStorageString !== String(current) // no need to overwrite anything if the default setting and local storage setting are already equal
    ) {
      // overwrite default store setting with local storage setting
      updateFn(localStorageString);
    } else if (
      doOverwriteLocalStorage &&
      localStorageString !== String(current) // no need to overwrite anything if the default setting and local storage setting are already equal
    ) {
      // overwrite local storage setting with default store setting
      window.localStorage.setItem(localStorageName, current.toString());
    }
  });
}

/** check if localStorage has not yet been populated for this device */
export function getIsFirstTimeSetup(): boolean {
  const isFirstTimeAppSetup = window.localStorage.getItem(
    "isFirstTimeAppSetup"
  );
  // If isFirstTimeAppSetup doesn't exist yet in local storage,
  // then the user's group metrics settings still need to be initialized once
  return isFirstTimeAppSetup === "true" || isFirstTimeAppSetup === null;
}

export function completeFirstTimeSetup() {
  window.localStorage.setItem("isFirstTimeAppSetup", "false");
}

/** for each setting in the query string (e.g. myurl.com/stuff?query1=true&query2=false),
 * apply those settings to the SettingsManager
 */
export function applySettingsFromQuery(querySettingsToApply: {
  [localStorageName: string]: [boolean, (bool: boolean) => void];
}) {
  const query = qs.parse(window.location.search);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      Object.entries(querySettingsToApply).forEach(([skey, svalue]) => {
        const [isValue, setIsValue] = svalue;
        if (key === skey) {
          // ONLY WORKS FOR BOOLEANS
          const qsIsValue = value === "true";
          // only update if necessary
          if (qsIsValue !== isValue) {
            setIsValue(qsIsValue);
          }
        }
      });
    });
  }
}
